import React from 'react';
import { Link } from 'react-router-dom';

/**
 * A reusable button component with consistent styling
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant (primary, secondary, accent, outline)
 * @param {string} props.size - Button size (sm, md, lg)
 * @param {boolean} props.block - Whether the button should be full width
 * @param {string} props.to - If provided, renders as a Link to this path
 * @param {string} props.href - If provided, renders as an anchor with this href
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {string} props.icon - Optional icon to display before text
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  block = false,
  to,
  href,
  disabled = false,
  className = '',
  children,
  onClick,
  icon,
  ...rest
}) => {
  // Determine the CSS classes based on props
  const classes = `
    btn
    btn-${variant}
    ${size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : ''}
    ${block ? 'btn-block' : ''}
    ${disabled ? 'disabled' : ''}
    ${className}
  `.trim();

  // Prepare content with optional icon
  const content = (
    <>
      {icon && <span className="btn-icon">{icon}</span>}
      <span className="btn-text">{children}</span>
    </>
  );

  // If 'to' prop is provided, render as a Link
  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {content}
      </Link>
    );
  }

  // If 'href' prop is provided, render as an anchor
  if (href) {
    return (
      <a href={href} className={classes} {...rest}>
        {content}
      </a>
    );
  }

  // Otherwise, render as a button
  return (
    <button
      className={classes}
      disabled={disabled}
      onClick={onClick}
      type={rest.type || 'button'}
      {...rest}
    >
      {content}
    </button>
  );
};

export default Button;
